import React, { useEffect, useState, useCallback } from "react";
import {
    HardDrive, Folder, FileText, ChevronRight, ChevronLeft,
    RefreshCw, Loader2, Server, AlertTriangle, Check, FolderPlus, Star,
} from "lucide-react";
import { http, errMsg, formatBytes } from "@/lib/api";

// Picks the separator already in use on this path so a new folder joins
// cleanly whether the agent reports Windows-style (`\`) or Azure/POSIX-style
// (`/`) paths; defaults to `/` for azure (flat, forward-slash prefixes) and
// `\` otherwise (local/NAS, both Windows agents).
function sepFor(p, kind) {
    if (p.includes("/") && !p.includes("\\")) return "/";
    if (p.includes("\\")) return "\\";
    return kind === "azure" ? "/" : "\\";
}

/**
 * FsBrowser — agent-initiated directory tree picker.
 *
 * Posts to /api/agents/{agentId}/fs/browse with {path, kind}, polls the
 * resulting /fs/result/{request_id} until status==="ready", and renders
 * the entries. Operator drills in by clicking directories (path becomes
 * the new query). Caller decides whether to display this as a folder
 * picker (onSelect callback fires when the operator clicks a directory's
 * select button) or a read-only browser.
 *
 * Props:
 *   agentId        — required. The target to browse — a real agent, or a
 *                    NAS connection record (see workerAgentId below).
 *   kind           — "local" | "nas" (default "local")
 *   onSelect       — optional ({path, type}) => void; renders an inline
 *                    "Select this folder" button next to each directory
 *   initialPath    — start at a specific path (default "" = agent root)
 *   workerAgentId  — required when agentId is a "nas"-kind connection
 *                    record: a real installed agent to do the connecting
 *                    + listing on its behalf. The backend dispatches the
 *                    fs-list command to this agent instead and returns
 *                    the id actually used, which this component then
 *                    polls against (see POST response's `agent_id`).
 */
export default function FsBrowser({ agentId, kind = "local", onSelect, initialPath = "", workerAgentId }) {
    const [path, setPath] = useState(initialPath);
    const [history, setHistory] = useState([]);
    const [entries, setEntries] = useState([]);
    const [status, setStatus] = useState("idle"); // idle | pending | ready | error
    const [error, setError] = useState("");
    const [creating, setCreating] = useState(false);
    const [newFolderName, setNewFolderName] = useState("");
    const [mkdirBusy, setMkdirBusy] = useState(false);
    const [mkdirError, setMkdirError] = useState("");

    // Polls the shared fs_browse_results channel until the dispatched agent
    // submits a result (used by both directory listing and mkdir, since
    // mkdir replies with the refreshed parent listing on the same channel).
    const pollResult = useCallback(async (pollAgentId, requestId) => {
        const start = Date.now();
        while (Date.now() - start < 30_000) {
            const { data: r } = await http.get(`/agents/${pollAgentId}/fs/result/${requestId}`);
            if (r.status === "ready") return r.entries || [];
            if (r.status === "error") throw new Error(r.error || "Agent reported an error");
            await new Promise((res) => setTimeout(res, 800));
        }
        throw new Error("Timed out waiting for agent — is it online?");
    }, []);

    const browse = useCallback(async (target) => {
        setStatus("pending");
        setError("");
        setEntries([]);
        try {
            const { data: req } = await http.post(`/agents/${agentId}/fs/browse`, {
                path: target,
                kind,
                worker_agent_id: workerAgentId || undefined,
            });
            // The dispatched agent may differ from `agentId` (a NAS
            // connection record has no software of its own — the backend
            // redirects to workerAgentId and reports back which id
            // actually received the command; that's who will reply).
            const pollAgentId = req.agent_id || agentId;
            const entries = await pollResult(pollAgentId, req.request_id);
            setEntries(entries);
            setStatus("ready");
        } catch (e) {
            setError(e.message || errMsg(e));
            setStatus("error");
        }
    }, [agentId, kind, workerAgentId, pollResult]);

    useEffect(() => {
        browse(path);
    }, [path, kind, browse]);

    // Root — for both "local" and "nas" kinds — is a virtual composite
    // view (quick-access folders + drives for local, share hosts for NAS),
    // not a real directory. The migration wizards default sourcePath/
    // destPath to the literal string "/" to mean "root", so check the
    // trimmed form rather than the raw string.
    const atVirtualRoot = (kind === "local" || kind === "nas") && !path.replace(/[\\/]+$/, "");

    const createFolder = async () => {
        const name = newFolderName.trim();
        if (!name || atVirtualRoot) return;
        setMkdirBusy(true);
        setMkdirError("");
        try {
            // Join without doubling a separator the path already ends
            // with — "/" + "/name" was the original bug (Windows parses
            // a leading "//" as a broken UNC path), but "/" itself must
            // stay: it's what tells the agent "current drive's root".
            const hasTrailingSep = /[\\/]$/.test(path);
            const newPath = !path ? name : hasTrailingSep ? `${path}${name}` : `${path}${sepFor(path, kind)}${name}`;
            const { data: req } = await http.post(`/agents/${agentId}/fs/mkdir`, {
                path: newPath,
                kind,
                worker_agent_id: workerAgentId || undefined,
            });
            const pollAgentId = req.agent_id || agentId;
            const entries = await pollResult(pollAgentId, req.request_id);
            setEntries(entries);
            setStatus("ready");
            setNewFolderName("");
            setCreating(false);
        } catch (e) {
            setMkdirError(e.message || errMsg(e));
        } finally {
            setMkdirBusy(false);
        }
    };

    const enter = (target) => {
        setHistory((h) => [...h, path]);
        setPath(target);
    };

    const back = () => {
        const prev = history[history.length - 1];
        setHistory((h) => h.slice(0, -1));
        setPath(prev || "");
    };

    return (
        <div className="space-y-3" data-testid="fs-browser">
            <div className="flex items-center gap-2">
                <button
                    onClick={back}
                    disabled={history.length === 0}
                    data-testid="fs-browser-back-btn"
                    className="rounded border border-zinc-800 p-1.5 text-zinc-400 hover:bg-zinc-800 disabled:opacity-30"
                >
                    <ChevronLeft size={14} />
                </button>
                <button
                    onClick={() => browse(path)}
                    data-testid="fs-browser-refresh-btn"
                    className="rounded border border-zinc-800 p-1.5 text-zinc-400 hover:bg-zinc-800"
                >
                    <RefreshCw size={14} className={status === "pending" ? "animate-spin" : ""} />
                </button>
                <div
                    className="flex-1 truncate rounded border border-zinc-800 bg-black/30 px-2 py-1.5 font-mono text-[11px] text-zinc-300"
                    data-testid="fs-browser-path"
                >
                    {kind === "nas" ? <Server size={10} className="mr-1 inline text-blue-400" /> : null}
                    {path || (kind === "nas" ? "(NAS roots)" : "(This PC)")}
                </div>
                <button
                    onClick={() => { setCreating((c) => !c); setMkdirError(""); }}
                    disabled={atVirtualRoot}
                    title={atVirtualRoot ? "Open a folder or drive first" : "New folder"}
                    data-testid="fs-browser-newfolder-btn"
                    className="rounded border border-zinc-800 p-1.5 text-zinc-400 hover:bg-zinc-800 disabled:opacity-30"
                >
                    <FolderPlus size={14} />
                </button>
            </div>

            {creating && (
                <div className="flex items-center gap-2" data-testid="fs-browser-newfolder-row">
                    <input
                        autoFocus
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") createFolder();
                            if (e.key === "Escape") { setCreating(false); setNewFolderName(""); setMkdirError(""); }
                        }}
                        placeholder="New folder name"
                        data-testid="fs-browser-newfolder-input"
                        className="flex-1 rounded border border-zinc-800 bg-black/30 px-2 py-1.5 text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-emerald-500/50 focus:outline-none"
                    />
                    <button
                        onClick={createFolder}
                        disabled={mkdirBusy || !newFolderName.trim()}
                        data-testid="fs-browser-newfolder-confirm"
                        className="inline-flex items-center gap-1 rounded border border-emerald-500/40 bg-emerald-500/10 px-2 py-1.5 text-[11px] text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-40"
                    >
                        {mkdirBusy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Create
                    </button>
                </div>
            )}
            {mkdirError && (
                <div className="flex items-start gap-2 text-xs text-rose-300" data-testid="fs-browser-newfolder-error">
                    <AlertTriangle size={13} className="mt-0.5" /> {mkdirError}
                </div>
            )}

            <div
                className="max-h-72 overflow-y-auto rounded border border-zinc-800 bg-zinc-950/40"
                data-testid="fs-browser-list"
            >
                {status === "pending" && (
                    <div className="flex items-center justify-center gap-2 p-6 text-xs text-zinc-400">
                        <Loader2 size={14} className="animate-spin" /> Asking agent for {path || "root"}…
                    </div>
                )}
                {status === "error" && (
                    <div className="flex items-start gap-2 p-4 text-xs text-rose-300">
                        <AlertTriangle size={14} className="mt-0.5" /> {error}
                    </div>
                )}
                {status === "ready" && entries.length === 0 && (
                    <div className="p-6 text-center text-xs text-zinc-500">Empty directory.</div>
                )}
                {status === "ready" && entries.map((e) => {
                    const isDir = e.type === "directory" || e.type === "drive" || e.type === "share-host";
                    const Icon = e.shortcut ? Star
                        : e.type === "drive" ? HardDrive
                        : e.type === "share-host" ? Server
                        : isDir ? Folder : FileText;
                    return (
                        <div
                            key={e.path}
                            data-testid={`fs-browser-entry-${e.name}`}
                            className="flex items-center gap-2 border-b border-zinc-800/60 px-3 py-2 text-xs last:border-0 hover:bg-zinc-800/30"
                        >
                            <Icon size={13} className={e.shortcut ? "text-cyan-400" : isDir ? "text-amber-400" : "text-zinc-400"} />
                            {isDir ? (
                                <button
                                    onClick={() => enter(e.path)}
                                    data-testid={`fs-browser-enter-${e.name}`}
                                    className="flex-1 truncate text-left text-zinc-200 hover:text-white"
                                >
                                    {e.name}
                                </button>
                            ) : (
                                <span className="flex-1 truncate font-mono text-zinc-300">{e.name}</span>
                            )}
                            {e.size_bytes != null && (
                                <span className="font-mono text-[10px] text-zinc-500">
                                    {formatBytes(e.size_bytes)}
                                </span>
                            )}
                            {isDir && onSelect && (
                                <button
                                    onClick={() => onSelect({ path: e.path, type: e.type })}
                                    data-testid={`fs-browser-select-${e.name}`}
                                    className="ml-2 inline-flex items-center gap-1 rounded border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-300 hover:bg-emerald-500/20"
                                >
                                    <Check size={10} /> Select
                                </button>
                            )}
                            {isDir && (
                                <ChevronRight size={11} className="text-zinc-600" />
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
